/**
 * Payment verification queue (F3-H, `ManageBilling`): the admin verifies or
 * rejects (with a reason) the payments the cashier recorded. Verified = immutable
 * (corrections via refund). Separation of duties: the cashier does NOT verify.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, X } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { Money } from "./money";
import { receiptUrl, usePayments, useRejectPayment, useVerifyPayment } from "../api";

export const AdminBilling: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const pending = usePayments(activeOrgId, () => "pending_verification");
  const verify = useVerifyPayment();
  const reject = useRejectPayment();
  const [rejecting, setRejecting] = createSignal<string | undefined>();
  const [reason, setReason] = createSignal("");

  const onVerify = (id: string) =>
    verify.mutate(id, {
      onSuccess: () => showToast(t(msg`Payment verified`), "success"),
      onError: (e) => showToast(e.message, "error"),
    });

  const onReject = (id: string) => {
    if (!reason().trim()) {
      showToast(t(msg`Enter a reason`), "error");
      return;
    }
    reject.mutate(
      { paymentId: id, reason: reason().trim() },
      {
        onSuccess: () => {
          showToast(t(msg`Payment rejected`), "success");
          setRejecting(undefined);
          setReason("");
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <div class="rounded-xl border border-border p-4">
      <h2 class="mb-3 font-semibold">{t(msg`Verification queue`)}</h2>
      <Show when={!pending.isLoading} fallback={<Skeleton class="h-24 w-full" />}>
        <Show
          when={(pending.data?.length ?? 0) > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`Nothing to verify`)}</p>}
        >
          <ul class="divide-y divide-border" data-testid="verification-queue">
            <For each={pending.data}>
              {(p) => (
                <li class="py-3 text-sm">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <span class="truncate font-medium">{p.studentUserId}</span>
                    <Money usdMinor={p.amountUsdMinor} />
                    <span class="text-xs text-muted-foreground">
                      {p.methodKey} · {p.currency} {(p.amountMinor / 100).toFixed(2)}
                      {p.referenceNumber ? ` · ${p.referenceNumber}` : ""}
                    </span>
                    <div class="flex items-center gap-2">
                      <Show when={p.receiptAssetKey}>
                        <a
                          href={receiptUrl(p.id)}
                          target="_blank"
                          rel="noopener"
                          class="text-xs text-primary hover:underline"
                        >
                          {t(msg`Receipt`)}
                        </a>
                      </Show>
                      <Button
                        size="sm"
                        onClick={() => onVerify(p.id)}
                        disabled={verify.isPending}
                        data-testid={`verify-${p.id}`}
                      >
                        <Check class="mr-1 h-4 w-4" />
                        {t(msg`Verify`)}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setRejecting(rejecting() === p.id ? undefined : p.id)}
                      >
                        <X class="mr-1 h-4 w-4" />
                        {t(msg`Reject`)}
                      </Button>
                    </div>
                  </div>
                  <Show when={rejecting() === p.id}>
                    <div class="mt-2 flex items-center gap-2">
                      <input
                        class="w-full rounded-lg border-2 border-border bg-field-background px-3 py-1.5 text-sm"
                        placeholder={t(msg`Reason for rejection`)}
                        value={reason()}
                        onInput={(e) => setReason(e.currentTarget.value)}
                      />
                      <Button size="sm" variant="destructive" onClick={() => onReject(p.id)}>
                        {t(msg`Confirm`)}
                      </Button>
                    </div>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
    </div>
  );
};
